import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, TextField, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PropTypes from 'prop-types';
import { fDateTime } from '../../../utils/formatTime';

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export default function OfficeComments({ comments, saving, onSave }) {
  const { t } = useTranslation();
  const authorName = useSelector((state) => state.user?.user?.name) || '';
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const persist = async (nextComments) => {
    await onSave(nextComments);
  };

  const addComment = async () => {
    const body = draft.trim();
    if (!body) return;
    const next = [
      ...comments,
      {
        id: newId(),
        body,
        author_name: authorName,
        created_at: new Date().toISOString(),
        updated_at: null,
      },
    ];
    await persist(next);
    setDraft('');
  };

  const saveEdit = async () => {
    const body = editingBody.trim();
    if (!body || !editingId) return;
    const next = comments.map((comment) => (
      comment.id === editingId
        ? { ...comment, body, updated_at: new Date().toISOString() }
        : comment
    ));
    await persist(next);
    setEditingId(null);
    setEditingBody('');
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const next = comments.filter((comment) => comment.id !== pendingDelete.id);
    await persist(next);
    if (editingId === pendingDelete.id) {
      setEditingId(null);
      setEditingBody('');
    }
    setPendingDelete(null);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('office_comments')}</Typography>
      {comments.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('no_office_comments')}
        </Typography>
      )}
      <Box sx={{ display: 'grid', gap: 1.5, mb: comments.length ? 2 : 0 }}>
        {comments.map((comment) => (
          <Box
            key={comment.id}
            sx={{ p: 1.5, border: '1px solid #e0e3e8', borderRadius: 1, background: '#f7f8fa' }}
          >
            {editingId === comment.id ? (
              <>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  value={editingBody}
                  onChange={(event) => setEditingBody(event.target.value)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                  <Button size="small" onClick={() => { setEditingId(null); setEditingBody(''); }} disabled={saving}>
                    {t('cancel_editing')}
                  </Button>
                  <LoadingButton
                    size="small"
                    variant="contained"
                    loading={saving}
                    disabled={!editingBody.trim()}
                    onClick={saveEdit}
                    sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
                  >
                    {t('save')}
                  </LoadingButton>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.body}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[
                      comment.author_name,
                      comment.created_at ? fDateTime(comment.created_at) : '',
                      comment.updated_at ? t('comment_edited') : '',
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  aria-label={t('edit')}
                  onClick={() => { setEditingId(comment.id); setEditingBody(comment.body || ''); }}
                  disabled={saving}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t('delete_comment')}
                  onClick={() => setPendingDelete(comment)}
                  disabled={saving}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        placeholder={t('office_comment_placeholder')}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!draft.trim()}
          onClick={addComment}
          sx={{ background: '#032C65', '&:hover': { background: '#021a40' } }}
        >
          {t('add_comment')}
        </LoadingButton>
      </Box>

      <Dialog open={Boolean(pendingDelete)} onClose={() => !saving && setPendingDelete(null)}>
        <DialogTitle>{t('delete_comment')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('delete_comment_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={saving}>{t('cancel_editing')}</Button>
          <LoadingButton color="error" variant="contained" loading={saving} onClick={confirmDelete}>
            {t('delete_comment')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

OfficeComments.propTypes = {
  comments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    body: PropTypes.string,
    author_name: PropTypes.string,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  })),
  saving: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

OfficeComments.defaultProps = {
  comments: [],
  saving: false,
};
